// YERİ: src/app/domains/website/sections/mkt-content.sections.ts
// Sayfanın gövdesi: özellik satırları (pastel kart), sektör ızgarası, metrikler,
// referanslar (fotoğraf + camsı alıntı kartı).
//
// PASTEL KARTLAR: düz beyaz kart dizisi sayfayı bir forma çeviriyordu. Her
// özellik satırı kendi yumuşak renk tonunda bir kartın içinde yaşıyor ve ürün
// ekranı kartın kenarından TAŞIYOR — bu, ekranı "yapıştırılmış" olmaktan
// çıkarıp sahnenin içine sokan detay.

import { Component, computed, inject, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { SiteConfig, cfg } from '@/app/domains/site/sections/section.model';
import { LanguageStore } from '../core/language.store';
import { MktMedia } from './mkt-media';
import { MktIcon } from './mkt-icon';
import { MktSlider } from './mkt-slider';
import { MktCountUp, MktReveal } from './mkt-motion';
import { MktRouterLink, resolveMktLink } from './mkt-link';
import { splitLines } from './mkt-price';

/** Pastel kart tonları — CSS'te .mkt-tone-* olarak tanımlı. */
const TONES = ['violet', 'blue', 'amber', 'mint'] as const;
type Tone = (typeof TONES)[number];

function toneClass(value: unknown, index: number): string {
  const tone = String(value ?? '') as Tone;
  const picked = TONES.includes(tone) ? tone : TONES[index % TONES.length];
  return `mkt-tone-${picked}`;
}

interface FeatureRow {
  eyebrow?: string;
  title?: string;
  body?: string;
  bullets?: string | string[];
  ctaText?: string;
  ctaUrl?: string;
  linkText?: string;
  linkUrl?: string;
  cardTone?: string;
  mediaType?: string;
  mediaUrl?: string;
  mediaAlt?: string;
  mockVariant?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// ÖZELLİK SATIRLARI — pastel kart, ekran kenardan taşar
// ═══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'mkt-feature-split',
  standalone: true,
  imports: [RouterLink, MktMedia, MktIcon, MktReveal, MktSlider],
  host: { class: 'block bg-[color:var(--mkt-paper)]' },
  template: `
    <div class="mx-auto max-w-7xl px-5 py-24 sm:px-8">

      @if (title()) {
        <div class="max-w-3xl" mktReveal>
          @if (eyebrow()) {
            <p class="mkt-eyebrow">{{ eyebrow() }}</p>
          }
          <h2 class="mkt-h2 mt-4 text-3xl sm:text-[2.75rem]/[1.18]">{{ title() }}</h2>
          @if (subtitle()) {
            <p class="mkt-lead mt-5">{{ subtitle() }}</p>
          }
        </div>
      }

      <!-- SLAYT. Kartlar üst üste yığılmak yerine yana kayıyor: sayfa üçte bir
           uzunluğa düşüyor ve bir sonraki kart kenardan görünerek "devamı var"
           diyor. Slaytların HEPSİ DOM'da — SSR ve SEO bozulmuyor. -->
      <div class="mt-14" mktReveal>
        <mkt-slider [count]="rows().length">
          @for (row of rows(); track $index; let i = $index) {
            <article class="overflow-hidden" [class]="tone(row, i)">
              <div class="grid items-center gap-8 lg:grid-cols-[1fr_1.1fr]">

                <div class="p-8 sm:p-10 lg:py-16 lg:ps-14">
                  @if (row.eyebrow) {
                    <p class="mkt-eyebrow">{{ row.eyebrow }}</p>
                  }
                  <h3 class="mkt-h3 mt-3 text-2xl sm:text-[1.9rem]/[1.28]">{{ row.title }}</h3>

                  @if (row.body) {
                    <p class="mt-4 text-[1.0625rem] leading-relaxed text-[color:var(--mkt-text-muted)]">
                      {{ row.body }}
                    </p>
                  }

                  @if (bulletsOf(row).length) {
                    <ul class="mt-6 space-y-2.5">
                      @for (bullet of bulletsOf(row); track $index) {
                        <li class="flex items-start gap-2.5">
                          <mkt-icon name="check" [size]="16"
                            class="mt-1 shrink-0 text-[color:var(--mkt-accent)]" />
                          <span class="text-[0.9375rem] text-[color:var(--mkt-text-muted)]">{{ bullet }}</span>
                        </li>
                      }
                    </ul>
                  }

                  @if (row.ctaText || row.linkText) {
                    <div class="mt-8 flex flex-wrap items-center gap-6">
                      @if (row.ctaText) {
                        <a [routerLink]="link(row.ctaUrl)" class="mkt-btn mkt-btn-primary !py-2.5 !text-sm">
                          {{ row.ctaText }}
                        </a>
                      }
                      @if (row.linkText) {
                        <a [routerLink]="link(row.linkUrl)" class="mkt-link !text-sm">
                          {{ row.linkText }}
                          <mkt-icon name="arrow" [size]="15" />
                        </a>
                      }
                    </div>
                  }
                </div>

                <!-- Ekran kartın DIŞ KENARINDAN TAŞAR: "yapıştırılmış görsel"
                     hissini kırar, sahnenin içine sokar. lg altında normale döner.
                     'start-0' (left-0 değil): RTL'de düzen aynalanır ve taşma
                     kartın diğer kenarından olmalı, yoksa görsel metnin üstüne biner. -->
                <div class="relative px-8 pb-10 lg:h-[26rem] lg:px-0 lg:pb-0">
                  <div class="lg:absolute lg:start-0 lg:top-1/2 lg:w-[126%] lg:-translate-y-1/2">
                    <mkt-media [config]="$any(row)" />
                  </div>
                </div>
              </div>
            </article>
          }
        </mkt-slider>
      </div>
    </div>
  `,
})
export class MktFeatureSplitSection {
  readonly config = input.required<SiteConfig>();

  private langStore = inject(LanguageStore);

  protected eyebrow  = computed(() => cfg(this.config(), 'eyebrow', ''));
  protected title    = computed(() => cfg(this.config(), 'title', ''));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));

  protected rows = computed<FeatureRow[]>(() => {
    const raw = this.config()['rows'];
    return Array.isArray(raw) ? raw : [];
  });

  protected tone(row: FeatureRow, index: number): string {
    return toneClass(row.cardTone, index);
  }

  /**
   * GERÇEK satır sonu ve LİTERAL `\n` ikisi de ayraç sayılır.
   *
   * Aynı alan veritabanında iki ayrı biçimde duruyor: özgün İngilizce seed
   * gerçek satır sonu kullanmış, sektör ve platform alt sayfaları literal `\n`.
   * Yalnız gerçek satır sonuna bölündüğü için o sayfalarda bütün maddeler tek
   * satıra çöküyor ve ekranda `\n` GÖRÜNÜYORDU — dört dilde, 31 sektör + 4
   * platform sayfasında. Düzeltmeyi veride değil burada yapmak bilinçli: CMS'e
   * metin yapıştıran birinin hangi biçimi getireceği kestirilemez.
   */
  protected bulletsOf(row: FeatureRow): string[] {
    return splitLines(row.bullets);
  }

  protected link(url?: string): MktRouterLink {
    return resolveMktLink(url ?? '', this.langStore.current());
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// SEKMELİ BÖLÜM — koyu zemin, tek bölümde birden çok hikâye
//
// Bir bölümde dört şey anlatmanın yolu dört bölüm değil, bir sekme. Sayfa
// kısalır ve ziyaretçi KENDİ ilgilendiği şeyi seçer.
// ═══════════════════════════════════════════════════════════════════════════

interface TabItem {
  tab?: string;
  title?: string;
  body?: string;
  bullets?: string | string[];
  ctaText?: string;
  ctaUrl?: string;
  mediaType?: string;
  mediaUrl?: string;
  mediaAlt?: string;
  mockVariant?: string;
}

@Component({
  selector: 'mkt-feature-tabs',
  standalone: true,
  imports: [RouterLink, MktMedia, MktIcon, MktReveal],
  host: { class: 'mkt-dark relative isolate block overflow-hidden mkt-glow mkt-grid-bg' },
  template: `
    <div class="relative z-10 mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-28">

      <div class="mx-auto max-w-3xl text-center" mktReveal>
        @if (eyebrow()) {
          <p class="mkt-eyebrow justify-center">{{ eyebrow() }}</p>
        }
        <h2 class="mkt-h2 mt-4 text-3xl sm:text-[3rem]/[1.18]">
          <span class="mkt-gradient-text">{{ title() }}</span>
        </h2>
        @if (subtitle()) {
          <p class="mkt-lead mt-6">{{ subtitle() }}</p>
        }
      </div>

      @if (items().length > 1) {
        <!-- Adımlar bir METRO HATTI olarak çizilir: geçilen kısım parlar,
             durulan istasyon büyür. Düz sekmeler bunların SIRALI olduğunu
             anlatmıyordu; bu bir süreç (önce domain, sonra site, sonra sistem). -->
        <ol class="mkt-metro mt-14" role="tablist"
          [style.--mkt-metro-steps]="items().length"
          [style.--mkt-metro-active]="active()">
          @for (item of items(); track $index; let i = $index) {
            <li class="mkt-metro-stop"
              [class.is-active]="active() === i"
              [class.is-done]="i < active()">
              <button type="button" role="tab"
                [attr.aria-selected]="active() === i"
                (click)="active.set(i)">
                <span class="mkt-metro-dot">{{ i + 1 }}</span>
                <span class="mkt-metro-label">{{ stopLabel(item) }}</span>
              </button>
            </li>
          }
        </ol>
      }

      @if (current(); as item) {
        <!-- Adımın MEDYASI YOKSA tek sütun ve ORTALI.
             İki sütunlu düzen, sağdaki medya kutusu boş kalınca metni sayfanın
             soluna sıkıştırıyor ve sağ yarısı bomboş duruyordu. Medya geldiğinde
             (mediaType/mockVariant) düzen kendiliğinden iki sütuna döner. -->
        <div class="mt-16"
          [class]="hasMedia(item)
            ? 'grid items-center gap-12 lg:grid-cols-2 lg:gap-20'
            : 'mx-auto max-w-3xl text-center'">

          <div>
            <h3 class="mkt-h3 text-2xl sm:text-[2rem]/[1.28]">{{ item.title }}</h3>

            @if (item.body) {
              <p class="mkt-lead mt-5">{{ item.body }}</p>
            }

            @if (bulletsOf(item).length) {
              <!-- Ortalı düzende madde işaretleri de ortalanır ama METİN sola
                   dayalı kalır: ortalanmış çok satırlı madde okunmuyor. -->
              <ul class="mt-7 space-y-3"
                [class.inline-flex]="!hasMedia(item)"
                [class.flex-col]="!hasMedia(item)"
                [class.text-start]="!hasMedia(item)">
                @for (bullet of bulletsOf(item); track $index) {
                  <li class="flex items-start gap-3">
                    <mkt-icon name="check" [size]="16"
                      class="mt-1 shrink-0 text-[color:var(--mkt-accent-glow)]" />
                    <span class="text-[color:var(--mkt-text-muted)]">{{ bullet }}</span>
                  </li>
                }
              </ul>
            }

            @if (item.ctaText) {
              <a [routerLink]="link(item.ctaUrl)" class="mkt-btn mkt-btn-secondary mt-9">
                {{ item.ctaText }}
                <mkt-icon name="arrow" [size]="15" />
              </a>
            }
          </div>

          @if (hasMedia(item)) {
            <div>
              <mkt-media [config]="$any(item)" />
            </div>
          }
        </div>
      }
    </div>
  `,
})
export class MktFeatureTabsSection {
  readonly config = input.required<SiteConfig>();

  private langStore = inject(LanguageStore);

  protected active = signal(0);

  protected eyebrow  = computed(() => cfg(this.config(), 'eyebrow', ''));
  protected title    = computed(() => cfg(this.config(), 'title', ''));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));

  protected items = computed<TabItem[]>(() => {
    const raw = this.config()['items'];
    return Array.isArray(raw) ? raw : [];
  });

  protected current = computed(() => this.items()[this.active()] ?? this.items()[0]);

  /**
   * İstasyon etiketi — baştaki numarayı ATAR.
   *
   * İçerik "1 · Connect" diye yazılmış (düz sekmelerde sırayı gösteren tek
   * şey oydu). Metro noktası numarayı zaten çiziyor; ikisi birden kalınca
   * "3 · 3 · Brand it" gibi okunuyordu. Veriyi değiştirmiyoruz: eski içerik
   * de, ileride numarasız yazılacak içerik de doğru görünsün.
   */
  /**
   * Adımın gösterecek bir görseli var mı.
   *
   * mkt-media, medya yokken HİÇBİR ŞEY çizmiyor (kesikli yer tutucu kutu
   * sayfayı wireframe'e çevirdiği için bilerek öyle). Bu yüzden iki sütunlu
   * düzende sağ sütun tamamen boş kalıyordu — düzeni burada, veriye bakarak
   * seçiyoruz.
   */
  protected hasMedia(item: TabItem): boolean {
    const record = item as unknown as Record<string, unknown>;
    const type = String(record['mediaType'] ?? 'none');

    return (type !== 'none' && !!record['mediaUrl']) || !!record['mockVariant'];
  }

  protected stopLabel(item: TabItem): string {
    const raw = (item.tab || item.title || '').trim();
    return raw.replace(/^\d+\s*[·.)\-–—]\s*/, '');
  }

  protected bulletsOf(item: TabItem): string[] {
    return splitLines(item.bullets);
  }

  protected link(url?: string): MktRouterLink {
    return resolveMktLink(url ?? '', this.langStore.current());
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// SEKTÖR IZGARASI
// ═══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'mkt-module-grid',
  standalone: true,
  imports: [RouterLink, MktIcon, MktReveal],
  host: { class: 'block bg-[color:var(--mkt-paper-soft)]' },
  template: `
    <div class="mx-auto max-w-7xl px-5 py-24 sm:px-8">

      <div class="mx-auto max-w-2xl text-center" mktReveal>
        @if (eyebrow()) {
          <p class="mkt-eyebrow justify-center">{{ eyebrow() }}</p>
        }
        <h2 class="mkt-h2 mt-4 text-3xl sm:text-[2.75rem]/[1.18]">{{ title() }}</h2>
        @if (subtitle()) {
          <p class="mkt-lead mt-5">{{ subtitle() }}</p>
        }
      </div>

      @if (searchable()) {
        <div class="mx-auto mt-10 max-w-md">
          <label class="sr-only" for="mkt-module-search">{{ searchPlaceholder() }}</label>
          <input id="mkt-module-search" type="search"
            class="w-full rounded-lg border border-[color:var(--mkt-border)] bg-[color:var(--mkt-surface)] px-4 py-3 text-[0.9375rem] outline-none transition focus:border-[color:var(--mkt-accent)] focus:ring-4 focus:ring-[color:var(--mkt-accent)]/10"
            [placeholder]="searchPlaceholder()"
            [value]="query()"
            (input)="query.set($any($event.target).value)" />
        </div>
      }

      <ul class="mt-12 grid gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
        @for (item of visible(); track item.name; let i = $index) {
          <li [mktReveal]="(i % 8) * 35">
            <a [routerLink]="link(item.url)"
              class="mkt-card mkt-card-hover group flex h-full flex-col p-5">
              <span class="mod-icon mb-4 grid size-10 place-items-center rounded-xl transition-colors duration-200">
                <mkt-icon [name]="item.icon || 'sparkles'" [size]="19" />
              </span>
              <span class="text-[0.9375rem] font-bold tracking-tight">{{ item.name }}</span>
              @if (item.description) {
                <span class="mt-1.5 text-[0.8125rem] leading-relaxed text-[color:var(--mkt-text-faint)]">
                  {{ item.description }}
                </span>
              }
            </a>
          </li>
        } @empty {
          <li class="col-span-full py-16 text-center text-[color:var(--mkt-text-faint)]">{{ emptyText() }}</li>
        }
      </ul>

      @if (hiddenCount() > 0) {
        <!-- Ana sayfada tam liste 30 kart demek: bölüm sayfayı yutuyor ve
             ziyaretçi aşağıdaki her şeyi kaçırıyor. İlk 8 gösterilir, gerisi
             bir tıkla açılır. ARAMA bu sınırı DELER — kişi kendi işini
             yazdığında kart ilk 8'de olmasa bile bulunur. -->
        <div class="mt-10 text-center">
          <button type="button" (click)="expanded.set(true)" class="mkt-btn mkt-btn-secondary">
            {{ moreText() }}
            <span class="text-[color:var(--mkt-text-faint)]">({{ hiddenCount() }})</span>
          </button>
        </div>
      }
    </div>
  `,
  styles: [`
    .mod-icon {
      background: color-mix(in srgb, var(--mkt-accent) 9%, transparent);
      color: var(--mkt-accent);
    }

    a:hover .mod-icon {
      background: var(--mkt-accent);
      color: #fff;
    }
  `],
})
export class MktModuleGridSection {
  readonly config = input.required<SiteConfig>();

  private langStore = inject(LanguageStore);

  protected query = signal('');
  protected expanded = signal(false);

  protected eyebrow           = computed(() => cfg(this.config(), 'eyebrow', ''));
  protected title             = computed(() => cfg(this.config(), 'title', ''));
  protected subtitle          = computed(() => cfg(this.config(), 'subtitle', ''));
  protected emptyText         = computed(() => cfg(this.config(), 'emptyText', 'No match.'));
  protected searchPlaceholder = computed(() => cfg(this.config(), 'searchPlaceholder', 'Search your industry…'));
  protected searchable        = computed(() => cfg<string>(this.config(), 'searchable', 'true') !== 'false');
  protected moreText          = computed(() => cfg(this.config(), 'moreText', 'More industries'));

  /** 0 / boş → sınır yok (sektör sayfası tam listeyi gösterir). */
  protected initialCount = computed(() => {
    const raw = Number(cfg<string>(this.config(), 'initialCount', ''));
    return Number.isFinite(raw) && raw > 0 ? raw : 0;
  });

  protected items = computed<{ name: string; description?: string; icon?: string; url?: string }[]>(() => {
    const raw = this.config()['items'];
    return Array.isArray(raw) ? raw : [];
  });

  /** 30 sektörü tek ekranda taramak zor — arama, ziyaretçinin kendi işini bulmasını sağlar. */
  private matching = computed(() => {
    const q = this.query().trim().toLowerCase();
    if (!q) return this.items();

    return this.items().filter((item) =>
      `${item.name} ${item.description ?? ''}`.toLowerCase().includes(q),
    );
  });

  /**
   * Kart sınırı YALNIZ arama boşken uygulanır.
   *
   * Aksi hâlde "Dental" yazan biri, kart ilk 8'in dışında kaldığı için
   * "yok" sonucunu alırdı — oysa modül var. Arama kutusunun tüm listede
   * çalışması, listeyi kısaltmanın ön şartı.
   */
  protected visible = computed(() => {
    const list = this.matching();
    const limit = this.initialCount();

    if (this.expanded() || this.query().trim() || limit === 0) return list;
    return list.slice(0, limit);
  });

  protected hiddenCount = computed(() => this.matching().length - this.visible().length);

  protected link(url?: string): MktRouterLink {
    return resolveMktLink(url ?? '', this.langStore.current());
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// METRİK BANDI
// ═══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'mkt-metrics',
  standalone: true,
  imports: [MktCountUp, MktReveal],
  host: { class: 'block border-y border-[color:var(--mkt-border)] bg-[color:var(--mkt-paper)]' },
  template: `
    <div class="mx-auto max-w-7xl px-5 py-20 sm:px-8">
      @if (title()) {
        <h2 class="mkt-h2 mx-auto mb-14 max-w-2xl text-center text-3xl sm:text-[2.25rem]/[1.18]" mktReveal>
          {{ title() }}
        </h2>
      }

      <dl class="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
        @for (item of items(); track $index; let i = $index) {
          <div class="text-center" [mktReveal]="i * 70">
            <dt class="sr-only">{{ item.label }}</dt>
            <dd>
              <!-- Sunucuda ve hareket-azaltmada sayaç SON DEĞERİ yazar: bir
                   metriğin "0" olarak indekslenmesi kabul edilemez. -->
              <span class="mkt-gradient-text block text-[2.75rem] font-extrabold tracking-tight sm:text-[3.25rem]"
                [mktCountUp]="+(item.value || 0)"
                [prefix]="item.prefix || ''"
                [suffix]="item.suffix || ''"
                [decimals]="+(item.decimals || 0)"></span>
              <span class="mt-2.5 block text-sm text-[color:var(--mkt-text-muted)]">{{ item.label }}</span>
            </dd>
          </div>
        }
      </dl>
    </div>
  `,
})
export class MktMetricsSection {
  readonly config = input.required<SiteConfig>();

  protected title = computed(() => cfg(this.config(), 'title', ''));
  protected items = computed<
    { value?: number | string; prefix?: string; suffix?: string; decimals?: number | string; label?: string }[]
  >(() => {
    const raw = this.config()['items'];
    return Array.isArray(raw) ? raw : [];
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// KANIT — pastel gradyan kap + DEV gradyan sayılar + yüzen mini kartlar
//
// Bir referansı ikna edici yapan övgü değil, SAYIDIR. Bu bölüm sayıyı sahnenin
// merkezine koyar: her sütunda dev bir oran, altında onu açıklayan tek cümle,
// ve aralarında ürünün o sayıyı NEREDEN aldığını gösteren yüzen mini kartlar.
// Müşterinin sözü sayının altında, kanıtın imzası olarak durur.
// ═══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'mkt-testimonials',
  standalone: true,
  imports: [RouterLink, MktIcon, MktReveal],
  host: { class: 'block bg-[color:var(--mkt-paper)]' },
  template: `
    <div class="mx-auto max-w-7xl px-5 py-24 sm:px-8">

      <!-- Pastel kap: bölümün tamamı tek bir yumuşak renk alanının içinde.
           Beyaz üstüne beyaz kart yerine, renkli bir sahne. -->
      <div class="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-50 via-indigo-50 to-violet-100 px-6 py-14 sm:px-12 sm:py-16"
        mktReveal>

        <div class="grid gap-6 lg:grid-cols-[1.15fr_1fr] lg:items-start">
          <h2 class="mkt-h2 text-3xl sm:text-[2.75rem]/[1.18]">{{ title() }}</h2>
          @if (subtitle()) {
            <p class="text-[color:var(--mkt-text-muted)] lg:pt-3">{{ subtitle() }}</p>
          }
        </div>

        <div class="relative mt-12 grid gap-6 lg:grid-cols-2">
          @for (item of items(); track $index; let i = $index) {
            <article class="flex flex-col rounded-2xl bg-white/70 p-8 backdrop-blur-sm"
              [mktReveal]="i * 80">

              <!-- Dev gradyan sayı: bölümün asıl kahramanı. -->
              @if (item.metric) {
                <p class="mkt-gradient-text text-[3.5rem] font-extrabold leading-none tracking-tight sm:text-[4.25rem]">
                  {{ item.metric }}
                </p>
              }
              @if (item.metricLabel) {
                <p class="mt-2 text-xl font-extrabold tracking-tight sm:text-2xl">{{ item.metricLabel }}</p>
              }

              <!-- Ürünün o sayıyı NEREDEN aldığını gösteren mini kartlar. -->
              @if (statsOf(item).length) {
                <div class="mt-8 flex flex-wrap gap-3">
                  @for (stat of statsOf(item); track $index) {
                    <div class="rounded-xl bg-white p-3.5 shadow-sm shadow-neutral-900/5">
                      <p class="text-[0.6875rem] font-semibold text-[color:var(--mkt-text-faint)]">{{ stat.label }}</p>
                      <p class="mt-1 flex items-baseline gap-1.5">
                        <span class="text-lg font-extrabold tracking-tight">{{ stat.value }}</span>
                        @if (stat.delta) {
                          <span class="text-[0.6875rem] font-bold text-emerald-600">▲ {{ stat.delta }}</span>
                        }
                      </p>
                    </div>
                  }
                </div>
              }

              <!-- Müşterinin sözü: sayının İMZASI. Bölümün altına, küçük. -->
              @if (item.quote) {
                <blockquote class="mt-auto pt-8">
                  <p class="text-[0.9375rem] leading-relaxed text-[color:var(--mkt-text-muted)]">
                    &ldquo;{{ item.quote }}&rdquo;
                  </p>
                  <footer class="mt-4 flex items-center gap-3">
                    @if (item.photoUrl) {
                      <img [src]="item.photoUrl" [alt]="item.author || ''"
                        class="size-9 rounded-full object-cover" loading="lazy" decoding="async" />
                    } @else {
                      <span class="grid size-9 place-items-center rounded-full text-[0.6875rem] font-bold text-white"
                        style="background: var(--mkt-gradient)">{{ initials(item.author) }}</span>
                    }
                    <div>
                      <p class="text-[0.8125rem] font-bold">{{ item.author }}</p>
                      <p class="text-[0.8125rem] text-[color:var(--mkt-text-faint)]">{{ item.role }}</p>
                    </div>
                  </footer>
                </blockquote>
              }
            </article>
          }
        </div>

        @if (allLinkText()) {
          <div class="mt-10 text-center">
            <a [routerLink]="link(allLinkUrl())" class="mkt-link">
              {{ allLinkText() }}
              <mkt-icon name="arrow" [size]="16" />
            </a>
          </div>
        }
      </div>
    </div>
  `,
})
export class MktTestimonialsSection {
  readonly config = input.required<SiteConfig>();

  private langStore = inject(LanguageStore);

  protected title       = computed(() => cfg(this.config(), 'title', ''));
  protected subtitle    = computed(() => cfg(this.config(), 'subtitle', ''));
  protected allLinkText = computed(() => cfg(this.config(), 'allLinkText', ''));
  protected allLinkUrl  = computed(() => cfg(this.config(), 'allLinkUrl', ''));

  protected items = computed<
    {
      metric?: string;
      metricLabel?: string;
      stats?: { label?: string; value?: string; delta?: string }[];
      quote?: string;
      author?: string;
      role?: string;
      photoUrl?: string;
    }[]
  >(() => {
    const raw = this.config()['items'];
    return Array.isArray(raw) ? raw : [];
  });

  protected statsOf(item: { stats?: { label?: string; value?: string; delta?: string }[] }) {
    return Array.isArray(item.stats) ? item.stats : [];
  }

  /** Fotoğraf yoksa baş harfler — kırık avatar ikonundan iyidir. */
  protected initials(author?: string): string {
    return (author ?? '?')
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('');
  }

  protected link(url?: string): MktRouterLink {
    return resolveMktLink(url ?? '', this.langStore.current());
  }
}
